interface TouchState {
  /** −1..1, +x right, +y up */
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
  fire1: boolean;
}

class VirtualStick {
  readonly root: HTMLDivElement;
  private base: HTMLDivElement;
  private knob: HTMLDivElement;
  private pointerId: number | null = null;
  x = 0;
  y = 0;

  constructor(side: "left" | "right") {
    this.root = document.createElement("div");
    this.root.className = `vstick vstick-${side}`;

    this.base = document.createElement("div");
    this.base.className = "vstick-base";

    this.knob = document.createElement("div");
    this.knob.className = "vstick-knob";

    this.base.appendChild(this.knob);
    this.root.appendChild(this.base);

    this.base.addEventListener("pointerdown", this.onDown);
    this.base.addEventListener("pointermove", this.onMove);
    this.base.addEventListener("pointerup", this.onUp);
    this.base.addEventListener("pointercancel", this.onUp);
  }

  private onDown = (e: PointerEvent): void => {
    if (this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.base.setPointerCapture(e.pointerId);
    this.updateFromEvent(e);
    e.preventDefault();
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.updateFromEvent(e);
    e.preventDefault();
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.x = 0;
    this.y = 0;
    this.knob.style.transform = "";
    e.preventDefault();
  };

  private updateFromEvent(e: PointerEvent): void {
    const rect = this.base.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = e.clientX - cx;
    let dy = e.clientY - cy;
    const len = Math.hypot(dx, dy);
    const max = rect.width / 2;
    if (len > max && len > 0) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }
    this.x = dx / max;
    // Flip so up = +1.
    this.y = -dy / max;
    this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
  }
}

class TouchButton {
  readonly root: HTMLDivElement;
  pressed = false;
  private pointerId: number | null = null;

  constructor(label: string, extraClass: string) {
    this.root = document.createElement("div");
    this.root.className = `vbtn ${extraClass}`;
    this.root.textContent = label;

    this.root.addEventListener("pointerdown", this.onDown);
    this.root.addEventListener("pointerup", this.onUp);
    this.root.addEventListener("pointercancel", this.onUp);
    this.root.addEventListener("pointerleave", this.onUp);
  }

  private onDown = (e: PointerEvent): void => {
    if (this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.root.setPointerCapture(e.pointerId);
    this.pressed = true;
    this.root.classList.add("active");
    e.preventDefault();
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.pressed = false;
    this.root.classList.remove("active");
    e.preventDefault();
  };
}

class TouchTapButton {
  readonly root: HTMLDivElement;
  private onTap: () => void;

  constructor(label: string, extraClass: string, onTap: () => void) {
    this.onTap = onTap;
    this.root = document.createElement("div");
    this.root.className = `vbtn ${extraClass}`;
    this.root.textContent = label;
    this.root.addEventListener("pointerdown", this.onDown);
  }

  setLabel(label: string): void {
    this.root.textContent = label;
  }
  setActive(active: boolean): void {
    this.root.classList.toggle("active", active);
  }

  private onDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.root.classList.add("active");
    window.setTimeout(() => this.root.classList.remove("active"), 120);
    this.onTap();
  };
}

export class TouchControls {
  readonly state: TouchState = {
    leftX: 0,
    leftY: 0,
    rightX: 0,
    rightY: 0,
    fire1: false,
  };

  private left = new VirtualStick("left");
  private right = new VirtualStick("right");
  private fire1Btn = new TouchButton("FIRE", "vbtn-fire1");
  private modeBtn: TouchTapButton;
  private autoBtn: TouchTapButton;

  onModeToggle: () => void = () => {};
  onAutoBrakeToggle: () => void = () => {};

  constructor(parent: HTMLElement = document.body) {
    this.modeBtn = new TouchTapButton("MODE", "vbtn-fire2", () => this.onModeToggle());
    this.autoBtn = new TouchTapButton("AUTO", "vbtn-auto", () => this.onAutoBrakeToggle());

    parent.appendChild(this.left.root);
    parent.appendChild(this.right.root);
    parent.appendChild(this.fire1Btn.root);
    parent.appendChild(this.modeBtn.root);
    parent.appendChild(this.autoBtn.root);
  }

  setVisible(v: boolean): void {
    const display = v ? "" : "none";
    this.left.root.style.display = display;
    this.right.root.style.display = display;
    this.fire1Btn.root.style.display = display;
    this.modeBtn.root.style.display = display;
    this.autoBtn.root.style.display = display;
  }

  syncModeButtons(fireMode: "combat" | "mining", autoBrake: boolean): void {
    this.modeBtn.setLabel(fireMode === "mining" ? "MINE" : "GUNS");
    this.autoBtn.setActive(autoBrake);
  }

  sample(): TouchState {
    this.state.leftX = this.left.x;
    this.state.leftY = this.left.y;
    this.state.rightX = this.right.x;
    this.state.rightY = this.right.y;
    this.state.fire1 = this.fire1Btn.pressed;
    return this.state;
  }
}
